import React from 'react';
import { Target, Eye, Heart, Users, Award, HeartHandshake, HelpingHand, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div className="min-h-screen">
      {/* Hero Banner */}
      <section className="relative bg-gradient-to-r from-blue-900 to-blue-700 text-white py-24 px-4">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1920')] bg-cover bg-center mix-blend-overlay opacity-30"></div>
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h1 className="text-5xl font-bold mb-4">About Talent Link Employment Agency</h1>
          <p className="text-xl text-blue-100">Building Africa's future through exceptional talent connections</p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-6 text-center">Who We Are</h2>
          <div className="text-lg text-gray-700 space-y-4">
            <p>
              Talent Link Employment Agency is a premier recruitment and human resources consultancy dedicated to connecting exceptional African talent with leading employers across key economic sectors. With deep expertise in Mining, Agriculture, Energy, Construction, Industrial, and Professional Services, we understand the unique challenges and opportunities in Africa's rapidly evolving job market.
            </p>
            <p>
              Founded on principles of professionalism, integrity, and impact, we go beyond traditional recruitment to become strategic partners in our clients' growth. Our rigorous screening processes, sector-specific knowledge, and commitment to ethical practices ensure that every placement contributes to organizational success and individual career advancement.
            </p>
            <p>
              We believe that employment is not just about filling positions—it's about creating opportunities that drive economic growth, foster social development, and build sustainable communities across Africa.
            </p>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-white p-10 rounded-xl shadow-lg border-t-4 border-blue-600">
              <div className="flex items-center gap-3 mb-4">
                <Eye className="text-blue-600" size={40} />
                <h3 className="text-2xl font-bold">Our Vision</h3>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">
                To be a leading and trusted employment agency in Africa, connecting exceptional talent with employers of choice while championing ethical recruitment, sustainable development, and inclusive economic growth.
              </p>
            </div>
            <div className="bg-white p-10 rounded-xl shadow-lg border-t-4 border-green-600">
              <div className="flex items-center gap-3 mb-4">
                <Target className="text-green-600" size={40} />
                <h3 className="text-2xl font-bold">Our Mission</h3>
              </div>
              <p className="text-gray-700 text-lg leading-relaxed">
                Talent Link Employment Agency is committed to delivering professional, transparent, and ethical recruitment solutions across Africa. We identify, develop, and connect exceptional talent with reputable employers, while promoting compliance, workplace wellbeing, diversity, and sustainable socio-economic impact in the communities we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center">Our Core Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Heart className="text-blue-600" size={48} />,
                title: 'Integrity',
                description: 'We operate with honesty, transparency, and fairness in all our dealings.'
              },
              {
                icon: <Award className="text-green-600" size={48} />,
                title: 'Professional Excellence',
                description: 'We uphold the highest recruitment standards and service delivery.'
              },
              {
                icon: <Eye className="text-purple-600" size={48} />,
                title: 'Accountability',
                description: 'We stand behind the quality of our placements and commitments.'
              },
              {
                icon: <Target className="text-red-600" size={48} />,
                title: 'Confidentiality',
                description: 'We protect client and candidate information with the utmost care.'
              },
              {
                icon: <Users className="text-orange-600" size={48} />,
                title: 'Inclusion',
                description: 'We promote equal opportunity and diversity in all placements.'
              },
              {
                icon: <HelpingHand className="text-indigo-600" size={48} />,
                title: 'Sustainable Impact',
                description: 'We support responsible and community-conscious employment solutions.'
              },
              {
                icon: <HeartHandshake className="text-teal-600" size={48} />,
                title: 'Partnership',
                description: 'We build enduring relationships based on trust and shared success.'
              }
            ].map((value, idx) => (
              <div key={idx} className="bg-gray-50 p-8 rounded-lg border border-gray-200 hover:shadow-xl transition-all">
                <div className="mb-4">{value.icon}</div>
                <h3 className="text-xl font-bold mb-3">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Commitment */}
      <section className="py-16 px-4 bg-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">Our Commitment</h2>
              <div className="space-y-4 text-gray-700 text-lg">
                <p className="font-semibold text-gray-800">
                  We believe employment is a driver of dignity, stability, and economic transformation.
                </p>
                <p>
                  <strong className="text-blue-600">Ethical Recruitment Practices:</strong> We uphold the highest standards of honesty and transparency in all our recruitment processes.
                </p>
                <p>
                  <strong className="text-green-600">Compliance with Labour Legislation:</strong> We ensure all placements adhere to local and international labour laws and regulations.
                </p>
                <p>
                  <strong className="text-purple-600">Workplace Safety & Wellbeing:</strong> We prioritize the health, safety, and welfare of every candidate we place.
                </p>
                <p>
                  <strong className="text-orange-600">Inclusion & Equal Opportunity:</strong> We champion diversity and ensure fair access to opportunities for all.
                </p>
                <p>
                  <strong className="text-indigo-600">Sustainable Employment Solutions:</strong> We align our operations with responsible business practices that contribute to long-term economic growth across Africa.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800" 
                alt="Team collaboration" 
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Our Promise</h2>
          <p className="text-xl mb-8 text-blue-100">
            At Talent Link Employment Agency, we do more than fill vacancies — we build careers, strengthen organisations, and contribute to Africa's economic advancement.
          </p>
          <Link 
            to="/register" 
            className="inline-flex items-center gap-2 bg-white text-blue-600 px-10 py-4 rounded-lg font-semibold hover:bg-blue-50 transition text-lg shadow-xl"
          >
            Contact Us Today
            <ArrowRight size={24} />
          </Link>
        </div>
      </section>
    </div>
  );
}
